import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import geohash from 'ngeohash'
// import { LITTER_MAP_FEATURES, LITTER_MAP_FEATURES_COUNT } from '@/endpoints'

import { geohashesFetching } from '@/slices/map'
import * as setUtils from '@/utils/setUtils'

// /////////////////////////////////////////////////////////////////////////////////////////
// ///////////////// Util Functions ////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

// If these are changed, the map visibilities in the map slice need to be updated
export function getFeatureTypeAndPrecisionFromZoom(zoomLevel) {
    if (zoomLevel >= 4 && zoomLevel < 7) {
        console.log('zoomLevel', zoomLevel, 'searching for large count')
        return { featureType: 'LARGE_COUNT', precision: 2 }
    }
    if (zoomLevel >= 7 && zoomLevel < 9) {
        console.log('zoomLevel', zoomLevel, 'searching for medium count')
        return { featureType: 'MEDIUM_COUNT', precision: 3 }
    }
    if (zoomLevel >= 9 && zoomLevel < 12) {
        console.log('zoomLevel', zoomLevel, 'searching for small count')
        return { featureType: 'SMALL_COUNT', precision: 4 }
    }
    if (zoomLevel >= 12 && zoomLevel <= 24) {
        console.log('zoomLevel', zoomLevel, 'searching for features')
        return { featureType: 'FEATURES', precision: 5 }
    }
    return { featureType: null, precision: null }
}

function calculateGeohashesForBounds(map, precision) {
    if (!precision) return []
    const bbox = map.getBounds()
    return geohash.bboxes(
        bbox._sw.lat,
        bbox._sw.lng,
        bbox._ne.lat,
        bbox._ne.lng,
        precision
    )
}

// /////////////////////////////////////////////////////////////////////////////////////////
// ////////////////////////////////////// Thunks ///////////////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

export const fetchByGeohash = createAsyncThunk(
    'map/fetchByGeohash',
    async ({ map, modelTypes }, { getState, dispatch, rejectWithValue }) => {
        const state = getState()

        console.log('fetchByGeohash start timer')
        const timeStart = performance.now()

        const zoomLevel = Math.floor(state.map.viewport.zoom)

        if (zoomLevel < 4) {
            return rejectWithValue('Too zoomed out - not fetching')
        }

        const { featureType, precision } =
            getFeatureTypeAndPrecisionFromZoom(zoomLevel)

        if (!featureType) {
            return rejectWithValue('Invalid zoom level')
        }

        const allGeohashes = calculateGeohashesForBounds(map, precision)

        // For each model type, find which geohashes haven't been fetched
        const modelTypesToFetch = []
        let geohashesToFetch = new Set()

        for (const modelType of modelTypes) {
            const key = `${modelType}:${featureType}`
            const tracker = state.map.geohashTracker[key] || {
                fetching: [],
                fetched: []
            }

            const unfetchedForType = setUtils.difference(
                new Set(allGeohashes),
                new Set([...tracker.fetched, ...tracker.fetching])
            )

            if (unfetchedForType.size > 0) {
                modelTypesToFetch.push(modelType)
                geohashesToFetch = setUtils.union(
                    geohashesToFetch,
                    unfetchedForType
                )
            }
        }

        if (modelTypesToFetch.length === 0) {
            console.log('no new model types need fetching')
            const timeToReject = performance.now()
            console.log(
                `Time taken to reject backend fetch: ${(timeToReject - timeStart) / 1000} seconds`
            )
            return rejectWithValue('No new geohashes to fetch')
        }

        geohashesToFetch = Array.from(geohashesToFetch)

        // Mark as fetching for each model type
        for (const modelType of modelTypesToFetch) {
            dispatch(
                geohashesFetching({
                    modelType,
                    featureType,
                    geohashes: geohashesToFetch
                })
            )
        }

        // Single API call with all model types
        // const URL =
        //     featureType === 'FEATURES'
        //         ? LITTER_MAP_FEATURES
        //         : LITTER_MAP_FEATURES_COUNT

        const params = new URLSearchParams()
        geohashesToFetch.forEach((g) => params.append('geohash', g))
        modelTypesToFetch.forEach((t) => params.append('type', t))
        if (featureType !== 'FEATURES') {
            params.append('precision', precision)
        }

        const response = await axios.get(`${URL}?${params.toString()}`)

        const timeEndFetch = performance.now()
        console.log(
            `Time taken to fetch from backend: ${(timeEndFetch - timeStart) / 1000} seconds`
        )

        console.log("we've reached the end of the thunk")
        console.log({
            featureType,
            modelTypes: modelTypesToFetch,
            response: response.data
        })

        return {
            featureType,
            modelTypes: modelTypesToFetch,
            geohashes: geohashesToFetch,
            response: response.data
        }
    }
)
